import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Instagram, Facebook, ArrowRight } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const footerLinks = {
  explore: [
    { name: 'Rooms & Suites', path: '/rooms' },
    { name: 'Dining Experience', path: '/dining' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Special Offers', path: '/offers' },
  ],
  discover: [
    { name: 'Explore Koraput', path: '/explore' },
    { name: 'Events & Weddings', path: '/events' },
    { name: 'Blog', path: '/blog' },
    { name: 'Tours & Travels', path: '/tours' },
    { name: 'Emerald AI', path: '/emerald-ai' },
  ],
  information: [
    { name: 'About Us', path: '/p/about' },
    { name: 'Contact Us', path: '/contact' },
    { name: 'Privacy Policy', path: '/p/privacy-policy' },
    { name: 'Terms of Service', path: '/p/terms-of-service' },
  ],
};

// Default contact info as fallback
const defaultContact = {
  phones: ['+91 6372 350 065', '+91 9437 082 665'],
  email: 'thehotelemerald@gmail.com',
  secondary_email: 'reservations@thehotelemerald.in',
  whatsapp: '+916372350065',
  address: 'NALCO Chowk, Semiliguda, Koraput, Odisha 764036, India'
};

export function Footer() {
  const currentYear = new Date().getFullYear();
  const [contact, setContact] = useState(defaultContact);

  // Fetch contact info from site settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/site-settings`);
        if (response.data?.contact) {
          setContact({
            ...defaultContact,
            ...response.data.contact
          });
        }
      } catch (error) {
        console.warn('Failed to fetch site settings for footer:', error);
      }
    };
    fetchSettings();
  }, []);

  // Format phone for tel: link (remove spaces)
  const formatPhoneLink = (phone) => phone.replace(/\s/g, '');

  return (
    <footer className="bg-emerald-950 text-emerald-100" data-testid="main-footer">
      {/* Main Footer */}
      <div className="container-custom section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img 
                src="/images/logo.png" 
                alt="The Hotel Emerald" 
                className="h-14 w-auto"
              />
              <span className="font-serif text-xl text-white tracking-wide">
                The Hotel Emerald
              </span>
            </Link>
            <p className="text-emerald-300 text-sm leading-relaxed mb-6">
              Nestled amidst the majestic hills of Koraput, The Hotel Emerald invites you to a world where luxury meets the untouched beauty of the Eastern Ghats.
            </p>
            <div className="flex gap-4">
              <a
                href="https://instagram.com/thehotelemerald"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-sm bg-emerald-800/50 flex items-center justify-center hover:bg-emerald-700 transition-colors"
                data-testid="social-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-sm bg-emerald-800/50 flex items-center justify-center hover:bg-emerald-700 transition-colors"
                data-testid="social-facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Explore Column */}
          <div>
            <h4 className="font-serif text-lg text-white mb-4">Explore</h4>
            <ul className="space-y-3">
              {footerLinks.explore.map((link) => (
                <li key={link.path + link.name}>
                  <Link
                    to={link.path}
                    className="text-emerald-300 hover:text-gold transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Discover Column */}
          <div>
            <h4 className="font-serif text-lg text-white mb-4">Discover</h4>
            <ul className="space-y-3">
              {footerLinks.discover.map((link) => (
                <li key={link.path + link.name}>
                  <Link
                    to={link.path}
                    className="text-emerald-300 hover:text-gold transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Information Column (merged with Contact) */}
          <div>
            <h4 className="font-serif text-lg text-white mb-4">Information</h4>
            <ul className="space-y-3 mb-6">
              {footerLinks.information.map((link) => (
                <li key={link.path + link.name}>
                  <Link
                    to={link.path}
                    className="text-emerald-300 hover:text-gold transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
            
            {/* Contact Info - Dynamic from API */}
            <div className="space-y-3 pt-4 border-t border-emerald-800/50">
              {contact.phones && contact.phones[0] && (
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-gold flex-shrink-0" />
                  <a 
                    href={`tel:${formatPhoneLink(contact.phones[0])}`} 
                    className="text-emerald-300 hover:text-white text-sm"
                    data-testid="footer-phone"
                  >
                    {contact.phones[0]}
                  </a>
                </div>
              )}
              {contact.email && (
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-gold flex-shrink-0" />
                  <a 
                    href={`mailto:${contact.email}`} 
                    className="text-emerald-300 hover:text-white text-xs"
                    data-testid="footer-email"
                  >
                    {contact.email}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-emerald-800">
        <div className="container-custom py-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-emerald-400 text-xs">
            © {currentYear} The Hotel Emerald. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/p/about" className="text-emerald-400 hover:text-white text-xs">
              About Us
            </Link>
            <Link to="/p/privacy-policy" className="text-emerald-400 hover:text-white text-xs">
              Privacy Policy
            </Link>
            <Link to="/p/terms-of-service" className="text-emerald-400 hover:text-white text-xs">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
